import React from 'react';
import { TrendingUp, TrendingDown, Calculator, Info, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PerformanceMethodology: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/employees/performance')}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors mr-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Performance Analytics
        </button>
      </div>

      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-6 text-white mb-6">
        <div className="flex items-center">
          <Calculator className="h-8 w-8 mr-3" />
          <div>
            <h1 className="text-2xl font-bold">Performance Calculation Methodology</h1>
            <p className="text-blue-100 mt-1">Understanding how employee performance scores are calculated</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Performance Score Components */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
            <h2 className="text-xl font-semibold text-blue-900 flex items-center">
              <Calculator className="h-5 w-5 mr-2" />
              Performance Score Components (0-100)
            </h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-lg p-4 border-l-4 border-green-500">
                  <h3 className="font-semibold text-green-800 mb-2">Hours Score (50% Weight)</h3>
                  <p className="text-green-700 text-sm mb-2">Most important component - directly impacts revenue</p>
                  <div className="text-sm text-green-600">
                    <p><strong>Formula:</strong> (Average Hours per Week ÷ 50) × 100</p>
                    <p><strong>Target:</strong> 50 hours/week = 100 points</p>
                    <p><strong>Example:</strong> 40 hrs/week = 80 points</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-4 border-l-4 border-blue-500">
                  <h3 className="font-semibold text-blue-800 mb-2">Consistency Score (25% Weight)</h3>
                  <p className="text-blue-700 text-sm mb-2">Rewards reliable, predictable work patterns</p>
                  <div className="text-sm text-blue-600">
                    <p><strong>Formula:</strong> 100 - (Standard Deviation ÷ Average Hours) × 100</p>
                    <p><strong>Best:</strong> Same hours each week = 100 points</p>
                    <p><strong>Penalty:</strong> High variance = lower score</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg p-4 border-l-4 border-purple-500">
                  <h3 className="font-semibold text-purple-800 mb-2">Activity Score (25% Weight)</h3>
                  <p className="text-purple-700 text-sm mb-2">Measures engagement and availability</p>
                  <div className="text-sm text-purple-600">
                    <p><strong>Formula:</strong> (Weeks Worked ÷ Total Weeks Available) × 100</p>
                    <p><strong>Example:</strong> 20 of 26 weeks = 77 points</p>
                    <p><strong>Best:</strong> Working every week = 100 points</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg p-4 border-l-4 border-orange-500">
                  <h3 className="font-semibold text-orange-800 mb-2">Volume Bonus (Up to +10)</h3>
                  <p className="text-orange-700 text-sm mb-2">Extra points for high total hours</p>
                  <div className="text-sm text-orange-600">
                    <p><strong>1000+ Total Hours:</strong> +10 bonus points</p>
                    <p><strong>500+ Total Hours:</strong> +5 bonus points</p>
                    <p><strong>Under 500 Hours:</strong> No bonus</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold text-gray-800 mb-2">Final Calculation Example:</h4>
              <div className="text-sm text-gray-600 font-mono">
                Employee works 45 hrs/week average, very consistent (95% consistency), worked 22/26 weeks, 900 total hours
                <br />
                <span className="text-green-600">Hours Score: (45÷50)×100 = 90 points × 0.50 = 45</span>
                <br />
                <span className="text-blue-600">Consistency Score: 95 points × 0.25 = 23.75</span>
                <br />
                <span className="text-purple-600">Activity Score: (22÷26)×100 = 85 points × 0.25 = 21.25</span>
                <br />
                <span className="text-orange-600">Volume Bonus: 500+ hours = +5 points</span>
                <br />
                <strong className="text-gray-800">Final Score: 45 + 23.75 + 21.25 + 5 = 95 points</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Impact Tiers */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-green-50 border-b border-green-200">
            <h2 className="text-xl font-semibold text-green-900">Revenue Impact Tiers</h2>
            <p className="text-green-700 text-sm mt-1">How average hours per week translates to business value</p>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="w-4 h-4 rounded-full bg-green-500 mr-3"></div>
                  <div>
                    <div className="font-semibold text-green-800">45+ hours/week</div>
                    <div className="text-sm text-green-600">High Revenue Generator</div>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="w-4 h-4 rounded-full bg-blue-500 mr-3"></div>
                  <div>
                    <div className="font-semibold text-blue-800">35-44 hours/week</div>
                    <div className="text-sm text-blue-600">Good Revenue Contributor</div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="w-4 h-4 rounded-full bg-yellow-500 mr-3"></div>
                  <div>
                    <div className="font-semibold text-yellow-800">25-34 hours/week</div>
                    <div className="text-sm text-yellow-600">Moderate Productivity</div>
                  </div>
                </div>
                
                <div className="flex items-center p-3 bg-red-50 rounded-lg border border-red-200">
                  <div className="w-4 h-4 rounded-full bg-red-500 mr-3"></div>
                  <div>
                    <div className="font-semibold text-red-800">Under 25 hours/week</div>
                    <div className="text-sm text-red-600">Underutilized Capacity</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Business Rationale */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-200">
            <h2 className="text-xl font-semibold text-yellow-900">Why Hours Matter Most (50% Weight)</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Revenue Direct Correlation</h4>
                    <p className="text-sm text-gray-600">Every billable hour directly increases company revenue. Higher hours = higher profits.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-blue-500 mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Resource Utilization</h4>
                    <p className="text-sm text-gray-600">Maximizes return on employee investment including salary, benefits, and training costs.</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-purple-500 mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Client Service Excellence</h4>
                    <p className="text-sm text-gray-600">Higher availability ensures better project coverage and client satisfaction.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-orange-500 mt-2 flex-shrink-0"></div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Growth Indicator</h4>
                    <p className="text-sm text-gray-600">Consistent high hours demonstrate employee engagement and commitment to company success.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trend Analysis */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Trend Analysis Methodology</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                <TrendingUp className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <h4 className="font-semibold text-green-800">Improving Trend</h4>
                <p className="text-sm text-green-600 mt-1">Recent 4 weeks average is more than 10% higher than previous 4 weeks</p>
              </div>
              
              <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                <TrendingDown className="h-8 w-8 text-red-600 mx-auto mb-2" />
                <h4 className="font-semibold text-red-800">Declining Trend</h4>
                <p className="text-sm text-red-600 mt-1">Recent 4 weeks average is more than 10% lower than previous 4 weeks</p>
              </div>
              
              <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="w-8 h-8 bg-gray-400 rounded-full mx-auto mb-2"></div>
                <h4 className="font-semibold text-gray-800">Stable Trend</h4>
                <p className="text-sm text-gray-600 mt-1">Recent performance is within 10% of previous period (consistent)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Notes */}
        <div className="bg-blue-50 rounded-lg p-6 border border-blue-200">
          <div className="flex items-start space-x-3">
            <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Important Notes</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Performance scores are recalculated in real-time based on selected time range</li>
                <li>• Only employees with recorded timesheet hours are included in rankings</li>
                <li>• Consistency scores reward predictable work patterns that aid in project planning</li>
                <li>• Volume bonuses recognize employees who contribute significantly to company revenue</li>
                <li>• Trend analysis requires at least 8 weeks of data for accurate calculation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PerformanceMethodology;